import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, tap, throwError } from 'rxjs';
import { IResponseLogin, IUserLogin } from '../interfaces/login';
import { noCheckToken, refreshTokenContext } from './auth-interceptor.service';
import { BrowserStorageService } from './browser-storage.service';
import { DeviceIdService } from './device-id.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  baseURL: string = environment.apiUrl.replace(/\/api$/, '');

  constructor(
    private http: HttpClient,
    private browserStorageService: BrowserStorageService,
    private deviceIdService: DeviceIdService,
  ) {}

  login(body: IUserLogin): Observable<HttpResponse<IResponseLogin>> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post<IResponseLogin>(this.baseURL + '/auth/login', body, {
      headers: headers,
      context: noCheckToken(),
      withCredentials: true,
      observe: 'response',
    });
  }

  recoverPassword(email: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseURL}/auth/send-password-recovery-email/${email}`,
      {
        context: noCheckToken(),
      },
    );
  }

  resetPassword(password: string): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.put(
      this.baseURL + '/auth/pass-reset',
      { password: password },
      { headers: headers },
    );
  }

  refreshToken() {
    // Intenta obtener el refresh token de localStorage
    let refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token found'));
    }
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http
      .post(
        `${this.baseURL}/auth/refresh?refreshToken=${refreshToken}`,
        {},
        {
          headers: headers,
          context: noCheckToken(),
        },
      )
      .pipe(
        tap((response: any) => {
          this.browserStorageService.set('token', response.data.access_token);
        }),
      );
  }

  /**
   * Único camino para cerrar sesión en la app: avisa al gateway y limpia el storage.
   *
   * Devuelve `void` y se subscribe solo a propósito. Antes devolvía un Observable que
   * ningún consumidor subscribía y, como los Observables de HttpClient son cold, la request
   * a `/auth/logout` no salía nunca: la sesión quedaba viva del lado del gateway.
   *
   * La request va marcada con `noCheckToken()` porque `AuthInterceptorService` es uno de los
   * que llama acá cuando el token y el refresh están vencidos. Sin esa marca, la request
   * pasaría por ese interceptor, volvería a encontrar el token vencido y llamaría a
   * `logout()` de nuevo: recursión infinita. Por eso el Bearer se arma a mano.
   */
  logout(): void {
    const token = this.browserStorageService.get('token');
    let headers = new HttpHeaders().set('Content-Type', 'application/json');
    if (token) {
      headers = headers.set('authorization', `Bearer ${token}`);
    }

    this.http
      .get(this.baseURL + '/auth/logout', {
        headers,
        context: noCheckToken(),
        withCredentials: true,
      })
      // Best-effort: si el gateway falla igual limpiamos del lado del browser.
      .subscribe({ error: () => {} });

    this.clearBrowserStorage();
  }

  /**
   * Barre localStorage dejando sólo lo que NO pertenece a la sesión.
   *
   * Es un clear con lista de preservación y no un borrado de claves puntuales a propósito:
   * si mañana alguien guarda datos del usuario en una clave nueva y se olvida de sumarla
   * acá, lo peor que pasa es que se borre. Con el criterio inverso, los datos del usuario
   * anterior sobrevivirían al logout.
   *
   * sessionStorage queda intacto: `AuthInterceptorService` deja ahí los flags `2fa_required`
   * y `2fa_setup_required` justo antes de llamar a este logout, y el login los lee al
   * arrancar. Limpiarlos acá rompería el flujo de 2FA.
   */
  private clearBrowserStorage(): void {
    const preserved = [this.deviceIdService.storageKey].map(
      (key) => [key, localStorage.getItem(key)] as const,
    );

    localStorage.clear();

    preserved.forEach(([key, value]) => {
      if (value !== null) {
        localStorage.setItem(key, value);
      }
    });
  }
  /**
   * La respuesta se observa entera porque una verificación exitosa con `rememberDevice: true`
   * devuelve el id de dispositivo confiable en el header `X-Device-Id` (CAT-4314). Sin
   * `observe: 'response'` el header es ilegible y el device nunca queda recordado en los
   * dominios de marca blanca, donde la cookie `deviceId` es de terceros y se descarta.
   */
  verify2FA(body: any): Observable<HttpResponse<any>> {
    return this.http.post<any>(`${this.baseURL}/auth/verify-2fa`, body, {
      withCredentials: true,
      observe: 'response',
    });
  }
  registerChildAgency(agencyId: string, body: any) {
    return this.http.put<any>(
      `${this.baseURL}/agencies/${agencyId}/partial-update`,
      body,
    );
  }
}
